import { type ClaiSchema } from '@clai/schema';
import { getCollectionFields } from '@lib/clai/utils/get-collection-fields';
import type { UploadProcessingProgressUpdate } from '@lib/upload-processing/types/upload-processing-progress-update';

import type { Flow } from '../types/flow';
import type { FlowDocumentMeta } from '../types/flow-meta';

/** Replays first-wave readiness from durable flow state when realtime messages
 *  landed before the client subscribed (common during deal-creation uploads). */
export function replayMissedFirstWaveFromFlow(
  flowId: string,
  pending: ReadonlySet<string>,
  flow: Flow,
  consumedFirstWave: Set<string>,
  onFirstWaveReady?: (update: UploadProcessingProgressUpdate) => void,
  onProcessingProgress?: (update: UploadProcessingProgressUpdate) => void
): void {
  const meta = flow.meta as FlowDocumentMeta | null | undefined;
  const collections = meta?.collections;
  const schema = flow.config as ClaiSchema | undefined;
  if (!collections || !schema?.collections?.length) {
    return;
  }

  for (const collectionId of pending) {
    if (consumedFirstWave.has(collectionId)) {
      continue;
    }

    const collectionMeta = collections[collectionId];
    if (
      !collectionMeta?.storageDocumentId ||
      collectionMeta.status !== 'processing'
    ) {
      continue;
    }

    const collection = schema.collections.find(
      candidate => candidate.id === collectionId
    );
    if (!collection) {
      continue;
    }

    const fieldsDetected = getCollectionFields(
      schema.fields ?? [],
      collection
    ).length;
    if (fieldsDetected === 0) {
      continue;
    }

    consumedFirstWave.add(collectionId);
    const update: UploadProcessingProgressUpdate = {
      flowId,
      collectionId,
      stage: 'fields',
      fieldsDetected,
      firstWaveReady: true,
    };
    onProcessingProgress?.(update);
    onFirstWaveReady?.(update);
  }
}
